import { gradeStep, } from '@stem/lab-schema/runtime';
export function initialState(script, tier, now = Date.now()) {
    const steps = {};
    script.steps.forEach((step, index) => {
        steps[step.id] = {
            // Only the first step is open. The lab is a sequence — Step 2 organises
            // what Step 1 collected — so letting a learner start at Step 3 would show
            // them an empty table and no way to fill it.
            status: index === 0 ? 'active' : 'locked',
            attempts: 0,
            hintIndex: 0,
            responses: {},
            scratch: {},
            results: null,
            earned: 0,
            possible: step.checks.reduce((sum, check) => sum + (check.formative ? 0 : check.points), 0),
            elapsedMs: 0,
            enteredAt: index === 0 ? now : null,
        };
    });
    return {
        scriptId: script.id,
        version: script.version,
        tier,
        currentStepId: script.steps[0].id,
        steps,
        startedAt: now,
        completedAt: null,
    };
}
function withStep(state, stepId, patch) {
    const current = state.steps[stepId];
    if (!current)
        return state;
    return { ...state, steps: { ...state.steps, [stepId]: { ...current, ...patch } } };
}
export function labReducer(state, action) {
    switch (action.type) {
        case 'hydrate':
            return action.state;
        case 'setTier':
            return { ...state, tier: action.tier };
        case 'enterStep': {
            const leaving = state.steps[state.currentStepId];
            const elapsed = leaving?.enteredAt != null
                ? leaving.elapsedMs + Math.max(0, action.at - leaving.enteredAt)
                : (leaving?.elapsedMs ?? 0);
            const next = {
                ...state,
                currentStepId: action.stepId,
                steps: {
                    ...state.steps,
                    ...(leaving ? { [state.currentStepId]: { ...leaving, elapsedMs: elapsed, enteredAt: null } } : {}),
                },
            };
            const target = next.steps[action.stepId];
            if (!target)
                return next;
            return {
                ...next,
                steps: {
                    ...next.steps,
                    [action.stepId]: {
                        ...target,
                        status: target.status === 'locked' ? 'active' : target.status,
                        enteredAt: action.at,
                    },
                },
            };
        }
        case 'setResponse': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            return withStep(state, action.stepId, {
                responses: { ...step.responses, [action.checkId]: action.value },
                // Editing an answer clears the previous marking. Leaving a red cross on
                // a cell the learner has just corrected reads as "still wrong".
                results: null,
            });
        }
        case 'setScratch': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            return withStep(state, action.stepId, {
                scratch: { ...step.scratch, [action.key]: action.value },
            });
        }
        case 'tally': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            const current = (step.responses[action.checkId] ?? {});
            const next = Math.max(0, (current[action.categoryKey] ?? 0) + action.delta);
            return withStep(state, action.stepId, {
                responses: {
                    ...step.responses,
                    [action.checkId]: { ...current, [action.categoryKey]: next },
                },
                results: null,
            });
        }
        case 'graded': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            const elapsed = step.enteredAt != null ? step.elapsedMs + Math.max(0, action.at - step.enteredAt) : step.elapsedMs;
            return withStep(state, action.stepId, {
                attempts: step.attempts + 1,
                results: action.result.results,
                earned: Math.max(step.earned, action.result.earned),
                possible: action.result.possible,
                status: action.result.passed ? 'passed' : 'active',
                elapsedMs: elapsed,
                enteredAt: action.at,
            });
        }
        case 'showHint': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            return withStep(state, action.stepId, { hintIndex: step.hintIndex + 1 });
        }
        case 'resetStep': {
            const step = state.steps[action.stepId];
            if (!step)
                return state;
            return withStep(state, action.stepId, {
                responses: {},
                results: null,
                // Attempts and hints are deliberately *not* reset. Clearing the table
                // is a legitimate way to start a count again; it is not a way to get a
                // fresh set of attempts on a step that limits them.
                status: 'active',
            });
        }
        case 'complete':
            return { ...state, completedAt: action.at };
        default:
            return state;
    }
}
/** Unlocks the next step once this one has passed. */
export function nextStepId(script, stepId) {
    const index = script.steps.findIndex(step => step.id === stepId);
    return index >= 0 && index < script.steps.length - 1 ? script.steps[index + 1].id : null;
}
export function toResponses(step, checkIds) {
    return checkIds.map(checkId => ({ checkId, value: step.responses[checkId] }));
}
/**
 * Grades locally, for immediate feedback.
 *
 * The same pure function the server calls on ingest. The runtime needs this to
 * paint the green glow with no round trip — which is what "Immediate feedback"
 * in the script means on a connection that takes two seconds — and the server
 * needs it because a client can claim any score it likes. Sharing the function
 * is what stops the two answers from ever differing.
 */
export function gradeLocally(script, state, stepId) {
    const step = script.steps.find(candidate => candidate.id === stepId);
    const stepState = state.steps[stepId];
    if (!step || !stepState)
        return { stepId, results: [], earned: 0, possible: 0, passed: false };
    return gradeStep(script, stepId, step.checks.map(check => ({ checkId: check.id, value: stepState.responses[check.id] })));
}
export function totalScore(state) {
    return Object.values(state.steps).reduce((total, step) => ({
        earned: total.earned + step.earned,
        possible: total.possible + step.possible,
    }), { earned: 0, possible: 0 });
}
export function progressFraction(state) {
    const steps = Object.values(state.steps);
    if (!steps.length)
        return 0;
    return steps.filter(step => step.status === 'passed').length / steps.length;
}
//# sourceMappingURL=state.js.map